import { sectionRenderer } from '@theme/section-renderer';
import Wishlist from '@theme/wishlist';

/**
 * `<wishlist-page>` — renders the visitor's saved products.
 *
 * The saved ids live in localStorage, so the cards can't be rendered on first
 * paint. On connect we read the ids and ask the Section Rendering API to render
 * this same section against the search URL (`?q=id:123 OR id:456`), which
 * populates the `search` object so the Liquid can render the framed print cards
 * (the `wishlist-cards` branch). Removing a card (via its heart) reconciles the
 * grid in place; emptying it reveals the empty state.
 */
class WishlistPage extends HTMLElement {
  /** @type {(() => void) | null} */
  #unsubscribe = null;

  connectedCallback() {
    this.grid = this.querySelector('[ref="grid"]');
    this.emptyState = this.querySelector('[ref="empty"]');
    this.loading = this.querySelector('[ref="loading"]');

    this.load();

    // Keep the grid in sync when the shopper unsaves an item while on the page.
    this.#unsubscribe = Wishlist.subscribe((ids) => this.#reconcile(ids));
  }

  disconnectedCallback() {
    this.#unsubscribe?.();
  }

  async load() {
    const ids = Wishlist.getIds();
    const sectionId = this.dataset.sectionId;

    if (ids.length === 0) {
      this.#showEmpty();
      return;
    }

    if (!sectionId) return;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    let markup;
    try {
      markup = await sectionRenderer.getSectionHTML(sectionId, false, url);
    } catch {
      this.#showEmpty();
      return;
    }

    const cards = markup && new DOMParser().parseFromString(markup, 'text/html').getElementById('wishlist-cards');

    if (!cards || cards.children.length === 0) {
      this.#showEmpty();
      return;
    }

    this.#showCards(cards.children);
  }

  /** @param {HTMLCollection} cards */
  #showCards(cards) {
    if (!this.grid) return;
    this.grid.replaceChildren(...Array.from(cards));
    this.grid.hidden = false;
    if (this.emptyState) this.emptyState.hidden = true;
    if (this.loading) this.loading.hidden = true;
  }

  #showEmpty() {
    if (this.grid) this.grid.hidden = true;
    if (this.loading) this.loading.hidden = true;
    if (this.emptyState) this.emptyState.hidden = false;
  }

  /**
   * Removes cards for products no longer saved; shows the empty state when the
   * last one goes. (Additions aren't handled live — a reload picks those up.)
   * @param {string[]} ids
   */
  #reconcile(ids) {
    if (!this.grid || this.grid.hidden) {
      if (ids.length === 0) this.#showEmpty();
      return;
    }

    const saved = new Set(ids.map(String));
    for (const card of Array.from(this.grid.children)) {
      const id = card.getAttribute('data-product-id') || card.querySelector('[data-product-id]')?.getAttribute('data-product-id');
      if (id && !saved.has(String(id))) card.remove();
    }

    if (this.grid.children.length === 0) this.#showEmpty();
  }
}

if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}

export default WishlistPage;
